import { useState } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FileUpload } from '@/components/ui/file-upload';
import { useUploadDocument } from '@/hooks/useApi';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function DocumentUpload() {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const uploadMutation = useUploadDocument();

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setUploadStatus({ type: null, message: '' });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    uploadMutation.mutate(selectedFile, {
      onSuccess: (data) => {
        setUploadStatus({
          type: 'success',
          message: `${data.file_name} has been uploaded successfully.`,
        });
        setSelectedFile(null);
        setTimeout(() => {
          setOpen(false);
          setUploadStatus({ type: null, message: '' });
        }, 2000);
      },
      onError: (error: any) => {
        setUploadStatus({
          type: 'error',
          message: error.message || 'Failed to upload document. Please try again.',
        });
      },
    });
  };

  const handleCancel = () => {
    setSelectedFile(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!uploadMutation.isPending) {
      setOpen(newOpen);
      if (!newOpen) {
        setSelectedFile(null);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
        >
          <Upload className="h-4 w-4" />
          Upload Document
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload PDF Document</DialogTitle>
          <DialogDescription>
            Upload a PDF document to your company's document storage. The file will be
            available for analysis and processing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {uploadStatus.type && (
            <Alert variant={uploadStatus.type === 'error' ? 'destructive' : 'default'}>
              <AlertDescription>{uploadStatus.message}</AlertDescription>
            </Alert>
          )}
          <FileUpload
            onFileSelect={handleFileSelect}
            onUpload={handleUpload}
            onCancel={handleCancel}
            selectedFile={selectedFile}
            isUploading={uploadMutation.isPending}
            accept=".pdf"
            maxSize={50 * 1024 * 1024}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
